import { Chat, Message } from './types';

/**
 * Where chat history lives. The provider only talks to this interface, so
 * moving history from the browser to the GraphQL API means writing another
 * adapter and passing it to `AISearchProvider`; nothing else changes.
 *
 * Everything is async because a remote store has to be. Chats are saved one at
 * a time (create and update are the same call) so an adapter maps straight onto
 * an upsert mutation and a delete mutation.
 */
export interface ChatStore {
  /** Every saved chat, with `createdAt`, `updatedAt` and message timestamps as Dates. */
  list(): Promise<Chat[]>;
  /** Creates the chat, or replaces it when its id is already stored. */
  save(chat: Chat): Promise<void>;
  remove(chatId: string): Promise<void>;
}

const STORAGE_KEY = 'ai-search-chats';

const revive = (chat: Chat): Chat => ({
  ...chat,
  createdAt: new Date(chat.createdAt),
  updatedAt: new Date(chat.updatedAt),
  messages: chat.messages.map((msg: Message) => ({
    ...msg,
    timestamp: new Date(msg.timestamp)
  }))
});

/**
 * The raw stored array, with entries that are not objects with an id dropped.
 * A blob that is not valid JSON, or not a list, is copied to a side key and
 * read as empty, so the next save starts a fresh blob instead of failing for
 * good. The copy keeps the unreadable text for support.
 */
const readRaw = (): Chat[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return [];
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(stored);
  } catch (error) {
    parsed = null;
  }
  if (!Array.isArray(parsed)) {
    console.error('Stored chats are unreadable; starting a fresh history.');
    try {
      localStorage.setItem(`${STORAGE_KEY}-unreadable`, stored);
    } catch (error) {
      // No room for the copy; the fresh history matters more.
    }
    return [];
  }
  return parsed.filter((c) => c && typeof c === 'object' && typeof c.id === 'string');
};

/** Browser-only history: one JSON blob in localStorage. */
export const localStorageChatStore: ChatStore = {
  async list() {
    let raw: Chat[];
    try {
      raw = readRaw();
    } catch (error) {
      // Storage itself is unavailable (disabled, or blocked by the browser).
      console.error('Error loading chats from localStorage:', error);
      return [];
    }
    // Revive per chat so one malformed entry does not hide the rest.
    return raw.flatMap((chat) => {
      try {
        return [revive(chat)];
      } catch (error) {
        console.error('Skipping unreadable chat:', error);
        return [];
      }
    });
  },

  async save(chat) {
    const chats = readRaw();
    const index = chats.findIndex((c) => c.id === chat.id);
    if (index === -1) {
      // Newest first, matching createNewChat.
      chats.unshift(chat);
    } else {
      chats[index] = chat;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(chats));
  },

  async remove(chatId) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(readRaw().filter((c) => c.id !== chatId)));
  }
};
